// frontend/src/pages/Inbox/OrderInbox.jsx
// ✅ VERSIÓN CORREGIDA - CON FIX DE TIMING Y CLEANUP

import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import PDFUploadZone from '../../components/Upload/PDFUploadZone';
import OrderConfirmModal from '../../components/Modals/OrderConfirmModal';
import InvoiceScheduleModal from '../../components/Modals/InvoiceScheduleModal';
import { ordersAPI, quotesAPI } from '../../services/api';


const OrderInbox = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedData, setUploadedData] = useState(null);
  const [quoteMatch, setQuoteMatch] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Estados para los 2 modales
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [invoiceScheduleModalOpen, setInvoiceScheduleModalOpen] = useState(false);

  // Datos pendientes de confirmación (NO guardados aún)
  const [pendingOrderData, setPendingOrderData] = useState(null);
  // Orden ya creada/actualizada (resultado de confirm)
  const [createdOrder, setCreatedOrder] = useState(null);
  // Schedule parcial existente cuando se actualiza una orden manual
  const [existingScheduleData, setExistingScheduleData] = useState(null);
  // ✅ NUEVO: Flag para indicar éxito
  const successRef = useRef(false);

  
  // Flag para indicar que debemos abrir el segundo modal
  const [shouldOpenScheduleModal, setShouldOpenScheduleModal] = useState(false);

  // useEffect para abrir el segundo modal cuando la orden esté lista
  useEffect(() => {
    if (shouldOpenScheduleModal && createdOrder) {
      console.log('🔄 Abriendo InvoiceScheduleModal...');
      setInvoiceScheduleModalOpen(true);
      setShouldOpenScheduleModal(false);
    }
  }, [shouldOpenScheduleModal, createdOrder]);

  // ✅ Función helper para limpiar archivos temporales
  const cleanupTempFiles = async (pdfPath, xmlPath) => {
    if (!pdfPath && !xmlPath) return;
    
    try {
      // Verificar que el método existe antes de llamarlo
      if (typeof ordersAPI.cleanupTemp === 'function') {
        await ordersAPI.cleanupTemp({
          pdf_path: pdfPath || '',
          xml_path: xmlPath || ''
        });
        console.log('🗑️ Archivos temporales limpiados');
      } else {
        console.warn('⚠️ ordersAPI.cleanupTemp no está disponible');
      }
    } catch (err) {
      console.error('⚠️ Error limpiando archivos temporales:', err);
      // No lanzamos el error, solo lo logueamos
    }
  };

  const handleUploadPDF = async (file) => {
    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('📤 Subiendo PDF de orden:', file.name);
      const formData = new FormData();
      formData.append('pdf_file', file);
      const response = await ordersAPI.uploadPDF(formData);
      console.log('✅ Respuesta completa:', response.data);

      if (response.data.success) {
        const extractedData = {
          ...response.data.data,
          temp_files: response.data.temp_files || { pdf_path: '', xml_path: '' }
        };

        console.log('📂 Datos con temp_files:', extractedData);

        setUploadedData(extractedData);

        // Enriquecer quote_match con datos completos de la propuesta
        const enrichedQuoteMatch = response.data.quote_match;

        // Si hay match, cargar datos completos de la propuesta
        if (enrichedQuoteMatch?.recommended?.id) {
          try {
            console.log('🔍 Cargando datos completos de propuesta:', enrichedQuoteMatch.recommended.id);
            const quoteDetailResponse = await quotesAPI.getById(enrichedQuoteMatch.recommended.id);
            enrichedQuoteMatch.recommended_full = quoteDetailResponse.data;
            console.log('✅ Datos completos de propuesta cargados:', quoteDetailResponse.data);
          } catch (err) {
            console.error('⚠️ Error al cargar detalle de propuesta:', err);
          }
        }

        setQuoteMatch(enrichedQuoteMatch);
        setOrderModalOpen(true);

      } else {
        throw new Error(response.data.error || 'Error al procesar PDF');
      }
    } catch (err) {
      console.error('❌ Error completo:', err);
      console.error('❌ Response data:', err.response?.data);
  
      let errorMsg = 'Error al procesar el PDF';
  
      if (err.response?.data?.error) {
        const errorData = err.response.data.error;
    
        if (typeof errorData === 'object' && !Array.isArray(errorData)) {
          errorMsg = Object.entries(errorData)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                return `${key}: ${value.join(', ')}`;
              }
              return `${key}: ${value}`;
            })
            .join('; ');
        } 
        else if (typeof errorData === 'string') {
          errorMsg = errorData;
        }
        else if (Array.isArray(errorData)) {
          errorMsg = errorData.join('; ');
        }
      } 
      else if (err.response?.data) {
        const errors = err.response.data;
        if (typeof errors === 'object') {
          errorMsg = Object.entries(errors)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                return `${key}: ${value.join(', ')}`;
              }
              return `${key}: ${value}`;
            })
            .join('; ');
        }
      } 
      else if (err.message) {
        errorMsg = err.message;
      }
  
      setError(errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  // PASO 1: Confirmar orden (primer modal) - CREA/ACTUALIZA LA ORDEN
  const handleConfirmOrder = async (confirmData) => {
    console.log('📋 Paso 1: Confirmando orden:', confirmData);

    try {
      const orderResponse = await ordersAPI.confirm(confirmData);
      const result = orderResponse.data;
      console.log('✅ Respuesta confirm:', result);

      const order = result.order;
      setCreatedOrder(order);

      // Prioridad de pre-relleno:
      // 1. Schedule parcial existente (flujo "Crear Orden Manual" con OC que llega después)
      // 2. manual_acceptance_data del quote (flujo "Aceptar sin OC" → OC llega después)
      const manualAcceptanceData = order.quote_details?.manual_acceptance_data || null;
      setExistingScheduleData(result.existing_schedule || manualAcceptanceData || null);

      // Cerrar primer modal
      setOrderModalOpen(false);

      if (result.updated_existing) {
        setSuccess(`OC vinculada a orden existente (${order.order_number}). Completa los datos de facturación.`);
      } else if (manualAcceptanceData) {
        setSuccess(`OC vinculada a cotización aceptada previamente (${order.order_number}). Algunos datos ya están pre-cargados.`);
      }

      // Activar flag para abrir segundo modal
      setTimeout(() => {
        setShouldOpenScheduleModal(true);
      }, 300);
    } catch (err) {
      console.error('❌ Error al confirmar orden:', err);
      let errorMsg = 'Error al crear la orden';
      if (err.response?.data?.error) {
        const ed = err.response.data.error;
        errorMsg = typeof ed === 'object' ? Object.entries(ed).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ') : ed;
      }
      throw new Error(errorMsg);
    }
  };


  // PASO 2: Programar facturación (segundo modal)
  const handleScheduleInvoice = async (scheduleData) => {
    try {
      console.log('📅 Paso 2: Programando facturación...');
      console.log('   • Orden:', createdOrder?.order_number);
      console.log('   • Datos de programación:', scheduleData);

      if (existingScheduleData?.id) {
        // Actualizar schedule parcial existente
        console.log('🔄 Actualizando schedule existente ID:', existingScheduleData.id);
        const api = (await import('../../services/api')).default;
        await api.patch(`/finance/invoice-schedules/${existingScheduleData.id}/`, scheduleData);
        console.log('✅ Schedule actualizado');
      } else {
        // Crear nuevo schedule
        console.log('🔄 Creando schedule para orden ID:', createdOrder.id);
        const scheduleResponse = await ordersAPI.scheduleInvoice(createdOrder.id, scheduleData);
        console.log('✅ Facturación programada:', scheduleResponse.data);
      }

      // Limpiar estados
      setCreatedOrder(null);
      setExistingScheduleData(null);
      setPendingOrderData(null);
      setUploadedData(null);
      successRef.current = true;

      setInvoiceScheduleModalOpen(false);
      setSuccess(`Orden ${createdOrder.order_number} lista con facturación programada`);

    } catch (err) {
      console.error('❌ Error programando facturación:', err);
      throw err;
    }
  };


  // Si el usuario cancela el PRIMER modal
  const handleCancelOrderModal = async () => {
    console.log('❌ Usuario canceló el primer modal');
    
    // Limpiar archivos temporales
    await cleanupTempFiles(
      uploadedData?.temp_files?.pdf_path,
      uploadedData?.temp_files?.xml_path
    );
    
    resetAllStates();
  };

  // Si el usuario cancela el SEGUNDO modal
  // Si el usuario cancela el SEGUNDO modal
  const handleCancelScheduleModal = async () => {
    // ✅ CORREGIDO: Usar ref en lugar de estado
    if (successRef.current) {
      console.log('✅ Modal cerrado después de éxito (sin cleanup)');
      successRef.current = false; // Reset para próximo uso
      return;
    }
  
    console.log('❌ Usuario canceló el segundo modal');
  
    await cleanupTempFiles(
      pendingOrderData?.pdf_file_path,
      pendingOrderData?.xml_file_path
    );
  
    resetAllStates();
  };


  // Función para limpiar todos los estados
  const resetAllStates = () => {
    setUploadedData(null);
    setQuoteMatch(null);
    setPendingOrderData(null);
    setCreatedOrder(null);
    setExistingScheduleData(null);
    setOrderModalOpen(false);
    setInvoiceScheduleModalOpen(false);
    setShouldOpenScheduleModal(false);
  };

  // Crear objeto "order" para el segundo modal
  const getOrderForScheduleModal = () => {
    // Si ya tenemos la orden creada, usarla
    if (createdOrder) return createdOrder;
    // Fallback con datos temporales (no debería ocurrir con el nuevo flujo)
    if (!uploadedData) return null;
    return {
      id: null,
      order_number: uploadedData.order_number || '',
      subtotal: uploadedData.total || '0',
      total: parseFloat(uploadedData.total || 0) * 1.16,
      pdf_file: uploadedData.temp_files?.pdf_path || '',
    };
  };


  return (
    <>
      {/* HEADER CON GRADIENTE MAS+ */}
      <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>

      <Container fluid className="py-0">
        {/* TÍTULO CON ICONOS */}
        <Row className="mb-4">
          <Col>
            <h6 className="h2 text-white d-inline-block mb-0">
              <i className="ni ni-bag-17 me-2"></i>
              Inbox - Órdenes de Compra
            </h6>
            <p className="text-white text-sm mb-0 opacity-8">
              Procesa órdenes de compra y vincula con propuestas automáticamente
            </p>
          </Col>
        </Row>

        {/* ALERTAS CON SOMBRAS */}
        {success && (
          <Row className="mb-4">
            <Col>
              <div className="alert alert-success alert-dismissible fade show shadow-sm" role="alert">
                <span className="alert-icon">
                  <i className="ni ni-like-2"></i>
                </span>
                <span className="alert-text">
                  <strong>¡Éxito!</strong> {success}
                </span>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setSuccess(null)}
                  aria-label="Close"
                ></button>
              </div>
            </Col>
          </Row>
        )}

        {error && (
          <Row className="mb-4">
            <Col>
              <div className="alert alert-danger alert-dismissible fade show shadow-sm" role="alert">
                <span className="alert-icon">
                  <i className="ni ni-support-16"></i>
                </span>
                <span className="alert-text">
                  <strong>Error:</strong> {error}
                </span>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label="Close"
                ></button>
              </div>
            </Col>
          </Row>
        )}

        {/* CONTENIDO PRINCIPAL */}
        <Row>
          <Col lg="8">
            <Card className="shadow border-0 mb-4">
              <CardHeader className="bg-transparent pb-0">
                <h6 className="text-uppercase text-xs font-weight-bold mb-0">
                  <i className="ni ni-cloud-upload-96 me-2 text-primary"></i>
                  Subir Orden de Compra
                </h6>
              </CardHeader>
              <CardBody className="p-3">
                <PDFUploadZone
                  onUpload={handleUploadPDF}
                  maxSize={10}
                  isUploading={isUploading}
                />
              </CardBody>
            </Card>
          </Col>

          <Col lg="4">
            {/* CARD DE INSTRUCCIONES CON TIMELINE */}
            <Card className="shadow border-0 mb-4">
              <CardHeader className="bg-gradient-primary pb-3">
                <h6 className="mb-0 text-white">
                  <i className="ni ni-bulb-61 me-2"></i>
                  Instrucciones
                </h6>
              </CardHeader>
              <CardBody className="p-3">
                <div className="timeline timeline-one-side">
                  <div className="timeline-block mb-3">
                    <span className="timeline-step bg-gradient-success">
                      <i className="ni ni-cloud-upload-96"></i>
                    </span>
                    <div className="timeline-content">
                      <small className="text-muted font-weight-bold text-uppercase">Paso 1</small>
                      <h6 className="text-sm mt-1 mb-0 font-weight-bold">Sube el PDF</h6>
                      <p className="text-xs mt-1 mb-0 text-muted">
                        Arrastra o selecciona la orden de compra
                      </p>
                    </div>
                  </div>

                  <div className="timeline-block mb-3">
                    <span className="timeline-step bg-gradient-info">
                      <i className="ni ni-compass-04"></i>
                    </span>
                    <div className="timeline-content">
                      <small className="text-muted font-weight-bold text-uppercase">Paso 2</small>
                      <h6 className="text-sm mt-1 mb-0 font-weight-bold">Búsqueda automática</h6>
                      <p className="text-xs mt-1 mb-0 text-muted">
                        Busca propuesta (tolerancia ±$1.00)
                      </p>
                    </div>
                  </div>

                  <div className="timeline-block mb-3">
                    <span className="timeline-step bg-gradient-warning">
                      <i className="ni ni-check-bold"></i>
                    </span>
                    <div className="timeline-content">
                      <small className="text-muted font-weight-bold text-uppercase">Paso 3</small>
                      <h6 className="text-sm mt-1 mb-0 font-weight-bold">Confirma la orden</h6>
                      <p className="text-xs mt-1 mb-0 text-muted">
                        Verifica datos y vincula propuesta
                      </p>
                    </div>
                  </div>

                  <div className="timeline-block">
                    <span className="timeline-step bg-gradient-primary">
                      <i className="ni ni-calendar-grid-58"></i>
                    </span>
                    <div className="timeline-content">
                      <small className="text-muted font-weight-bold text-uppercase">Paso 4</small>
                      <h6 className="text-sm mt-1 mb-0 font-weight-bold">Programa facturación</h6>
                      <p className="text-xs mt-1 mb-0 text-muted">
                        <strong className="text-danger">Obligatorio</strong> - Define fechas y responsable
                      </p>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* CARD DE INFORMACIÓN */}
            <Card className="shadow border-0">
              <CardHeader className="bg-transparent pb-0">
                <h6 className="text-uppercase text-xs font-weight-bold mb-0">
                  <i className="ni ni-bullet-list-67 me-2 text-primary"></i>
                  Información
                </h6>
              </CardHeader>
              <CardBody className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-3 pb-3" style={{ borderBottom: '1px solid #dee2e6' }}>
                  <span className="text-sm font-weight-bold">Tamaño máximo</span>
                  <span className="badge badge-sm bg-gradient-info">
                    <i className="ni ni-ruler-pencil me-1"></i>
                    10 MB
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3 pb-3" style={{ borderBottom: '1px solid #dee2e6' }}>
                  <span className="text-sm font-weight-bold">Formato aceptado</span>
                  <span className="badge badge-sm bg-gradient-success">
                    <i className="ni ni-single-copy-04 me-1"></i>
                    PDF
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3 pb-3" style={{ borderBottom: '1px solid #dee2e6' }}>
                  <span className="text-sm font-weight-bold">Tolerancia de match</span>
                  <span className="badge badge-sm bg-gradient-warning">
                    <i className="ni ni-compass-04 me-1"></i>
                    ±$1.00
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-sm font-weight-bold">Programación</span>
                  <span className="badge badge-sm bg-gradient-danger">
                    <i className="ni ni-bell-55 me-1"></i>
                    Obligatoria
                  </span>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* MODAL 1: Confirmación de Orden */}
      <OrderConfirmModal
        isOpen={orderModalOpen}
        toggle={handleCancelOrderModal}
        data={uploadedData}
        quoteMatch={quoteMatch}
        onConfirm={handleConfirmOrder}
      />

      {/* MODAL 2: Programación de Facturación */}
      <InvoiceScheduleModal
        isOpen={invoiceScheduleModalOpen}
        toggle={handleCancelScheduleModal}
        order={getOrderForScheduleModal()}
        quoteData={quoteMatch?.recommended_full || quoteMatch?.recommended || {}}
        onConfirm={handleScheduleInvoice}
        prefillData={existingScheduleData}
      />
    </>
  );
};

export default OrderInbox;
